/**
 * The route table. This is the C ↔ D handshake (CONTRACT.md §3): a route for
 * **every** member of `RoomPhaseSchema` exists here from day one, so Agent D
 * only ever adds files under `screens/game/` and updates the registry.
 *
 *   /                          landing — create a room, or join one
 *   /join                      4-letter code entry
 *   /r/:roomCode               the room (socket + onboarding gate + phase sync)
 *     /photo                   selfie or camera-roll upload (the selfie button
 *                              is the explicit camera grant, SPEC §6)
 *     /name                    12 chars, profanity filtered
 *     /lobby                   QR + code (SPEC §5.1), roster, settings, start
 *     /question-select         ┐
 *     /rank-and-predict        │
 *     /lock-countdown          │
 *     /reveal                  ├ Agent D, via screens/game/registry.tsx
 *     /justification           │
 *     /round-leaderboard       │
 *     /superlatives            │
 *     /ended                   ┘
 */
import { createElement } from 'react';
import { Navigate, Outlet, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { GAME_SCREENS } from '../screens/game/registry.js';
import { CaptureScreen } from '../screens/join/CaptureScreen.js';
import { JoinCodeScreen } from '../screens/join/JoinCodeScreen.js';
import { LandingScreen } from '../screens/join/LandingScreen.js';
import { NameScreen } from '../screens/join/NameScreen.js';
import { LobbyScreen } from '../screens/lobby/LobbyScreen.js';
import { GAME_PHASES } from './gameScreens.js';
import { RoomRoute } from './RoomRoute.js';
import { Shell } from './Shell.js';

/** One route per game phase, wired to whatever Agent D has registered. */
const gamePhaseRoutes: RouteObject[] = GAME_PHASES.map((phase) => ({
  path: phase,
  element: createElement(GAME_SCREENS[phase]),
}));

export const routes: RouteObject[] = [
  {
    element: (
      <Shell>
        <Outlet />
      </Shell>
    ),
    children: [
      { index: true, element: <LandingScreen /> },
      { path: 'join', element: <JoinCodeScreen /> },
      {
        path: 'r/:roomCode',
        element: <RoomRoute />,
        children: [
          // PhaseSync replaces this immediately; it exists so a bare /r/CODE is
          // never a dead end while the first snapshot is in flight.
          { index: true, element: <Navigate to="lobby" replace /> },
          { path: 'photo', element: <CaptureScreen /> },
          { path: 'name', element: <NameScreen /> },
          { path: 'lobby', element: <LobbyScreen /> },
          ...gamePhaseRoutes,
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
